import React from 'react'
import { Box, Typography, Chip } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Clock, ArrowRight } from 'lucide-react'
import { useSearchTokens } from '../../SearchScreen/searchDesignTokens'

const TYPE_COLORS = {
  constitution: '#22c55e',
  dissolution: '#ef4444',
  modification_du_capital: '#f59e0b',
  cession_de_parts: '#8b5cf6',
  changement_de_gerant: '#3b82f6',
  transfert_de_siege: '#06b6d4',
  fusion: '#ec4899',
  transformation: '#14b8a6',
  prorogation: '#64748b',
  dissolution_anticipee: '#dc2626',
  modification_statuts: '#f97316',
}

const CompanyTimeline = ({ announcements }) => {
  const { t } = useTranslation()
  const dk = useSearchTokens()
  const navigate = useNavigate()

  if (!announcements || announcements.length === 0) return null

  // Sort by date descending
  const sorted = [...announcements].sort((a, b) => {
    const da = a.date_publication ? new Date(a.date_publication) : new Date(0)
    const db = b.date_publication ? new Date(b.date_publication) : new Date(0)
    return db - da
  })

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  const handleClick = (annonceId) => {
    navigate(`/legal-notice/annonce/${encodeURIComponent(annonceId)}`)
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        borderRadius: '12px',
        border: `1px solid ${dk.border}`,
        backgroundColor: dk.surface,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Clock size={16} color={dk.gold} />
        <Typography
          sx={{
            fontSize: '0.82rem',
            fontWeight: 700,
            color: dk.text1,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {t('company_timeline', 'Historique des annonces')}
        </Typography>
        <Typography
          sx={{
            fontSize: '0.68rem',
            fontWeight: 600,
            color: dk.text3,
            backgroundColor: dk.chipBg,
            borderRadius: '10px',
            px: 0.8,
            py: '1px',
          }}
        >
          {announcements.length}
        </Typography>
      </Box>

      {/* Timeline */}
      <Box sx={{ position: 'relative', pl: 3 }}>
        {/* Vertical line */}
        <Box
          sx={{
            position: 'absolute',
            left: 8,
            top: 4,
            bottom: 4,
            width: 2,
            backgroundColor: dk.border,
            borderRadius: 1,
          }}
        />

        {sorted.map((item, idx) => {
          const typeColor = TYPE_COLORS[item.type_annonce] || dk.gold
          return (
            <Box
              key={item.annonce_id || idx}
              onClick={() => item.annonce_id && handleClick(item.annonce_id)}
              sx={{
                position: 'relative',
                pb: idx < sorted.length - 1 ? 2 : 0,
                cursor: item.annonce_id ? 'pointer' : 'default',
                '&:hover': item.annonce_id
                  ? {
                      '& .timeline-card': {
                        borderColor: dk.borderHover,
                        backgroundColor: dk.hover,
                      },
                    }
                  : {},
              }}
            >
              {/* Dot */}
              <Box
                sx={{
                  position: 'absolute',
                  left: -22,
                  top: 8,
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: typeColor,
                  border: `2px solid ${dk.surface}`,
                  boxShadow: `0 0 0 2px ${typeColor}35`,
                  zIndex: 1,
                }}
              />

              {/* Card */}
              <Box
                className="timeline-card"
                sx={{
                  p: 1.5,
                  borderRadius: '8px',
                  border: `1px solid ${dk.borderLight}`,
                  backgroundColor: dk.elevated,
                  transition: 'all 0.15s ease',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={t(`type_${item.type_annonce}`, item.type_annonce)}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        color: typeColor,
                        backgroundColor: `${typeColor}12`,
                        border: `1px solid ${typeColor}30`,
                        '& .MuiChip-label': { px: 0.8 },
                      }}
                    />
                    {item.forme_juridique && (
                      <Typography sx={{ fontSize: '0.68rem', color: dk.text3, fontWeight: 500 }}>
                        {t(`forme_${item.forme_juridique}`, item.forme_juridique)}
                      </Typography>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '0.7rem', color: dk.text3, fontWeight: 500 }}>
                    {formatDate(item.date_publication)}
                  </Typography>
                </Box>

                {item.denomination && (
                  <Typography
                    sx={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: dk.text1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item.denomination}
                  </Typography>
                )}

                {item.annonce_id && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.5 }}>
                    <Typography sx={{ fontSize: '0.65rem', color: dk.gold, fontWeight: 500 }}>
                      {t('view_detail', 'Voir le détail')}
                    </Typography>
                    <ArrowRight size={10} color={dk.gold} />
                  </Box>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

export default CompanyTimeline
